import type { Profile } from "./incidentProfiles";
export const additionalIds = [
  "brand-pkw",
  "brand-hecke",
  "brand-container",
  "brand-keller",
  "brand-garage",
  "brand-kueche",
  "brand-dach",
  "brand-mehrfamilie",
  "brand-landwirtschaft",
  "brand-pflegeheim",
  "brand-industrie",
  "brand-block",
  "manv-10",
  "manv-20",
  "manv-30",
  "manv-40",
  "manv-50",
  "rd-brustschmerz",
  "rd-schlaganfall",
  "rd-krampfanfall",
  "rd-sturz",
  "rd-kind",
  "rd-allergie",
  "rd-unterzucker",
  "rd-synkope",
  "rd-blutung",
  "rd-verbrennung",
  "rd-bauch",
  "rd-vergiftung",
  "rd-geburt",
  "rd-sport",
  "rd-hitze",
  "rd-alkohol",
  "rd-psychisch",
  "vu-rad",
  "vu-motorrad",
  "vu-fussgaenger",
  "vu-lkw",
  "vu-mehrere",
  "pol-einbruch",
  "pol-diebstahl",
  "pol-randale",
  "pol-vermisst",
  "pol-verkehr",
  "pol-bedrohung",
  "pol-geisel",
  "pol-gruppe",
  "th-tuer",
  "th-aufzug",
  "th-oel",
  "th-gas",
  "th-einsturz",
  "th-wasser",
  "th-baum",
  "wasser-bootsunfall",
  "berg-absturz",
] as const;
export const additionalProfiles: Record<
  (typeof additionalIds)[number],
  Profile
> = {
  "brand-pkw": {
    type: "brand",
    code: "B1",
    title: "PKW-Brand",
    report: "Ein Auto brennt am Straßenrand. Alle Personen sind draußen.",
    needs: {
      LF: 1,
      FuStW: 1,
    },
    weight: 8,
    priority: 2,
    workSeconds: 240,
  },
  "brand-hecke": {
    type: "brand",
    code: "B1",
    title: "Heckenbrand",
    report:
      "Eine Hecke brennt. Die Flammen sind noch von den Häusern entfernt.",
    needs: {
      LF: 1,
    },
    weight: 8,
    priority: 2,
    workSeconds: 240,
  },
  "brand-container": {
    type: "brand",
    code: "B1",
    title: "Containerbrand",
    report: "Ein Müllcontainer raucht und brennt. Niemand ist verletzt.",
    needs: {
      LF: 1,
    },
    weight: 8,
    priority: 2,
    workSeconds: 240,
  },
  "brand-keller": {
    type: "brand",
    code: "B2",
    title: "Kellerbrand",
    report:
      "Aus dem Keller kommt dichter Rauch. Die Bewohner gehen aus dem Haus.",
    needs: {
      HLF: 2,
      RTW: 1,
    },
    weight: 15,
    priority: 2,
    workSeconds: 240,
    patients: {
      count: 1,
      critical: 0,
      specialty: "general",
    },
  },
  "brand-garage": {
    type: "brand",
    code: "B2",
    title: "Garagenbrand",
    report:
      "In einer Garage brennt es. Ein Auto steht darin und die Flammen schlagen aus dem Tor.",
    needs: {
      HLF: 2,
      FuStW: 1,
    },
    weight: 8,
    priority: 2,
    workSeconds: 240,
  },
  "brand-kueche": {
    type: "brand",
    code: "B2",
    title: "Küchenbrand",
    report:
      "In der Küche brennt es. Eine Person hat Rauch eingeatmet und ist draußen.",
    needs: {
      HLF: 1,
      DLK: 1,
      RTW: 1,
    },
    weight: 20,
    priority: 2,
    workSeconds: 240,
    patients: {
      count: 1,
      critical: 0,
      specialty: "general",
    },
  },
  "brand-dach": {
    type: "brand",
    code: "B3",
    title: "Dachstuhlbrand",
    report:
      "Der Dachstuhl brennt. Zwei Bewohner husten stark, alle sind vor dem Gebäude.",
    needs: {
      HLF: 2,
      DLK: 1,
      RTW: 2,
      ELW: 1,
    },
    weight: 8,
    priority: 1,
    workSeconds: 240,
    patients: {
      count: 2,
      critical: 1,
      specialty: "burn",
    },
  },
  "brand-mehrfamilie": {
    type: "brand",
    code: "B3",
    title: "Mehrfamilienhaus · Menschen in Gefahr",
    report:
      "Rauch im ganzen Treppenhaus. Drei Menschen stehen an den Fenstern und kommen nicht heraus.",
    needs: {
      HLF: 2,
      DLK: 1,
      RTW: 3,
      NEF: 1,
      FuStW: 1,
    },
    weight: 8,
    priority: 1,
    workSeconds: 240,
    patients: {
      count: 3,
      critical: 1,
      specialty: "burn",
    },
  },
  "brand-landwirtschaft": {
    type: "brand",
    code: "B4",
    title: "Landwirtschaftliches Anwesen",
    report:
      "Eine große Scheune steht in Flammen. Es gibt Tiere im Stall und zwei verletzte Menschen.",
    needs: {
      HLF: 3,
      TLF: 2,
      ELW: 1,
      RTW: 2,
      NEF: 1,
    },
    weight: 3,
    priority: 1,
    workSeconds: 600,
    patients: {
      count: 2,
      critical: 1,
      specialty: "trauma",
    },
  },
  "brand-pflegeheim": {
    type: "brand",
    code: "B4",
    title: "Pflegeheimbrand",
    report:
      "Im Pflegeheim brennt ein Zimmer. Der Flur ist voller Rauch, mehrere Bewohner brauchen Hilfe.",
    needs: {
      HLF: 3,
      DLK: 2,
      ELW: 1,
      RTW: 5,
      NEF: 2,
      "SEG-Betreuung": 1,
    },
    weight: 2,
    priority: 1,
    workSeconds: 600,
    patients: {
      count: 5,
      critical: 2,
      specialty: "burn",
    },
  },
  "brand-industrie": {
    type: "brand",
    code: "B5",
    title: "Industriebrand · Abschnittsbildung",
    report:
      "Eine große Fabrik brennt. Es knallt und es gibt viel schwarzen Rauch. Beschäftigte sind verletzt.",
    needs: {
      HLF: 5,
      DLK: 2,
      TLF: 2,
      ELW: 2,
      RTW: 6,
      NEF: 2,
      FuStW: 3,
      "GW-San": 1,
    },
    weight: 1,
    priority: 1,
    workSeconds: 780,
    patients: {
      count: 6,
      critical: 3,
      specialty: "burn",
    },
  },
  "brand-block": {
    type: "brand",
    code: "B5",
    title: "Übergreifender Gebäudebrand",
    report:
      "Das Feuer greift auf die Nachbarhäuser über. Viele Menschen verlassen die Gebäude, einige sind verletzt.",
    needs: {
      HLF: 5,
      DLK: 3,
      ELW: 2,
      RTW: 8,
      NEF: 3,
      "SEG-Betreuung": 2,
      FuStW: 3,
    },
    weight: 1,
    priority: 1,
    workSeconds: 900,
    patients: {
      count: 8,
      critical: 3,
      specialty: "burn",
    },
  },
  "manv-10": {
    type: "vu",
    code: "MANV 10",
    title: "Unfall mit einem Kleinbus · MANV 10",
    report:
      "Unfall mit einem Kleinbus. Hier sind ungefähr 10 verletzte oder erkrankte Menschen. Mehrere liegen am Boden, andere können noch laufen.",
    needs: {
      RTW: 4,
      NEF: 2,
      ELW: 1,
      "GW-San": 1,
      "SEG-Betreuung": 1,
      HLF: 2,
      FuStW: 2,
    },
    weight: 0.3,
    priority: 1,
    workSeconds: 680,
    patients: {
      count: 10,
      critical: 2,
      specialty: "trauma",
    },
  },
  "manv-20": {
    type: "medizinisch",
    code: "MANV 20",
    title: "Rauchentwicklung bei einer Veranstaltung · MANV 20",
    report:
      "Rauchentwicklung bei einer Veranstaltung. Hier sind ungefähr 20 verletzte oder erkrankte Menschen. Mehrere liegen am Boden, andere können noch laufen.",
    needs: {
      RTW: 4,
      NEF: 2,
      ELW: 1,
      "GW-San": 1,
      "SEG-Betreuung": 1,
      HLF: 2,
      FuStW: 2,
    },
    weight: 0.3,
    priority: 1,
    workSeconds: 760,
    patients: {
      count: 20,
      critical: 4,
      specialty: "general",
    },
  },
  "manv-30": {
    type: "vu",
    code: "MANV 30",
    title: "Busunfall · MANV 30",
    report:
      "Busunfall. Hier sind ungefähr 30 verletzte oder erkrankte Menschen. Mehrere liegen am Boden, andere können noch laufen.",
    needs: {
      RTW: 6,
      NEF: 3,
      ELW: 2,
      "GW-San": 1,
      "SEG-Betreuung": 1,
      HLF: 2,
      FuStW: 2,
    },
    weight: 0.3,
    priority: 1,
    workSeconds: 840,
    patients: {
      count: 30,
      critical: 6,
      specialty: "trauma",
    },
  },
  "manv-40": {
    type: "medizinisch",
    code: "MANV 40",
    title: "Brand in einer Unterkunft · MANV 40",
    report:
      "Brand in einer Unterkunft. Hier sind ungefähr 40 verletzte oder erkrankte Menschen. Mehrere liegen am Boden, andere können noch laufen.",
    needs: {
      RTW: 8,
      NEF: 4,
      ELW: 2,
      "GW-San": 2,
      "SEG-Betreuung": 2,
      HLF: 2,
      FuStW: 2,
    },
    weight: 0.3,
    priority: 1,
    workSeconds: 920,
    patients: {
      count: 40,
      critical: 8,
      specialty: "general",
    },
  },
  "manv-50": {
    type: "medizinisch",
    code: "MANV 50",
    title: "Schweres Ereignis bei einer Großveranstaltung · MANV 50",
    report:
      "Schweres Ereignis bei einer Großveranstaltung. Hier sind ungefähr 50 verletzte oder erkrankte Menschen. Mehrere liegen am Boden, andere können noch laufen.",
    needs: {
      RTW: 10,
      NEF: 5,
      ELW: 2,
      "GW-San": 2,
      "SEG-Betreuung": 2,
      HLF: 2,
      FuStW: 2,
    },
    weight: 0.3,
    priority: 1,
    workSeconds: 1000,
    patients: {
      count: 50,
      critical: 10,
      specialty: "trauma",
    },
  },
  "rd-brustschmerz": {
    type: "medizinisch",
    code: "RD2",
    title: "Brustschmerz",
    report: "Mein Mann hat starke Schmerzen in der Brust und ist ganz blass.",
    needs: {
      RTW: 1,
      NEF: 1,
    },
    weight: 10,
    priority: 1,
    workSeconds: 240,
    patients: {
      count: 1,
      critical: 1,
      specialty: "cardiac",
    },
  },
  "rd-schlaganfall": {
    type: "medizinisch",
    code: "RD2",
    title: "Verdacht auf Schlaganfall",
    report:
      "Meine Frau kann plötzlich nicht richtig sprechen und ein Arm ist ganz schwach.",
    needs: {
      RTW: 1,
      NEF: 1,
    },
    weight: 10,
    priority: 1,
    workSeconds: 240,
    patients: {
      count: 1,
      critical: 1,
      specialty: "stroke",
    },
  },
  "rd-krampfanfall": {
    type: "medizinisch",
    code: "RD2",
    title: "Krampfanfall",
    report:
      "Eine Person hat am ganzen Körper gekrampft und ist jetzt sehr benommen.",
    needs: {
      RTW: 1,
      NEF: 1,
    },
    weight: 10,
    priority: 1,
    workSeconds: 240,
    patients: {
      count: 1,
      critical: 1,
      specialty: "general",
    },
  },
  "rd-sturz": {
    type: "medizinisch",
    code: "RD1",
    title: "Sturzverletzung",
    report:
      "Ich bin auf der Treppe gestürzt. Mein Bein tut sehr weh, ich kann nicht aufstehen.",
    needs: {
      RTW: 1,
    },
    weight: 16,
    priority: 2,
    workSeconds: 240,
    patients: {
      count: 1,
      critical: 0,
      specialty: "trauma",
    },
  },
  "rd-kind": {
    type: "medizinisch",
    code: "RD2",
    title: "Kindernotfall",
    report: "Mein Kind bekommt schlecht Luft und ist sehr erschöpft.",
    needs: {
      RTW: 1,
      NEF: 1,
    },
    weight: 10,
    priority: 1,
    workSeconds: 240,
    patients: {
      count: 1,
      critical: 1,
      specialty: "pediatric",
    },
  },
  "rd-allergie": {
    type: "medizinisch",
    code: "RD2",
    title: "Schwere allergische Reaktion",
    report:
      "Nach einem Insektenstich wird das Gesicht immer dicker und die Atmung schwer.",
    needs: {
      RTW: 1,
      NEF: 1,
    },
    weight: 10,
    priority: 1,
    workSeconds: 240,
    patients: {
      count: 1,
      critical: 1,
      specialty: "general",
    },
  },
  "rd-unterzucker": {
    type: "medizinisch",
    code: "RD2",
    title: "Bewusstseinsstörung",
    report:
      "Mein Vater ist kaum ansprechbar. Er ist Diabetiker, mehr weiß ich nicht.",
    needs: {
      RTW: 1,
      NEF: 1,
    },
    weight: 10,
    priority: 1,
    workSeconds: 240,
    patients: {
      count: 1,
      critical: 1,
      specialty: "general",
    },
  },
  "rd-synkope": {
    type: "medizinisch",
    code: "RD1",
    title: "Kreislaufkollaps",
    report:
      "Eine Frau ist kurz umgefallen. Jetzt ist sie wieder wach, aber sehr schwach.",
    needs: {
      RTW: 1,
    },
    weight: 16,
    priority: 2,
    workSeconds: 240,
    patients: {
      count: 1,
      critical: 0,
      specialty: "general",
    },
  },
  "rd-blutung": {
    type: "medizinisch",
    code: "RD2",
    title: "Starke Blutung",
    report:
      "Jemand hat sich tief verletzt. Es blutet stark und der Verband wird immer wieder nass.",
    needs: {
      RTW: 1,
      NEF: 1,
    },
    weight: 10,
    priority: 1,
    workSeconds: 240,
    patients: {
      count: 1,
      critical: 1,
      specialty: "trauma",
    },
  },
  "rd-verbrennung": {
    type: "medizinisch",
    code: "RD2",
    title: "Verbrennung",
    report:
      "Heißes Wasser ist über einen Menschen gelaufen. Die Haut ist stark verbrannt.",
    needs: {
      RTW: 1,
      NEF: 1,
    },
    weight: 10,
    priority: 1,
    workSeconds: 240,
    patients: {
      count: 1,
      critical: 1,
      specialty: "burn",
    },
  },
  "rd-bauch": {
    type: "medizinisch",
    code: "RD1",
    title: "Akute Bauchschmerzen",
    report: "Ich habe plötzlich starke Bauchschmerzen und muss mich übergeben.",
    needs: {
      RTW: 1,
    },
    weight: 16,
    priority: 2,
    workSeconds: 240,
    patients: {
      count: 1,
      critical: 0,
      specialty: "general",
    },
  },
  "rd-vergiftung": {
    type: "medizinisch",
    code: "RD2",
    title: "Vergiftungsverdacht",
    report:
      "Eine Person hat versehentlich eine unbekannte Flüssigkeit getrunken und fühlt sich sehr schlecht.",
    needs: {
      RTW: 1,
      NEF: 1,
    },
    weight: 10,
    priority: 1,
    workSeconds: 240,
    patients: {
      count: 1,
      critical: 1,
      specialty: "general",
    },
  },
  "rd-geburt": {
    type: "medizinisch",
    code: "RD2",
    title: "Ungeplante Geburt",
    report: "Die Wehen kommen sehr schnell. Ich glaube, das Kind kommt jetzt.",
    needs: {
      RTW: 1,
      NEF: 1,
    },
    weight: 10,
    priority: 1,
    workSeconds: 240,
    patients: {
      count: 1,
      critical: 1,
      specialty: "pediatric",
    },
  },
  "rd-sport": {
    type: "medizinisch",
    code: "RD1",
    title: "Sportverletzung",
    report: "Beim Sport ist jemand umgeknickt. Der Fuß ist stark geschwollen.",
    needs: {
      RTW: 1,
    },
    weight: 16,
    priority: 2,
    workSeconds: 240,
    patients: {
      count: 1,
      critical: 0,
      specialty: "trauma",
    },
  },
  "rd-hitze": {
    type: "medizinisch",
    code: "RD2",
    title: "Hitzenotfall",
    report: "Ein Mann ist bei der Hitze zusammengebrochen und antwortet kaum.",
    needs: {
      RTW: 1,
      NEF: 1,
    },
    weight: 10,
    priority: 1,
    workSeconds: 240,
    patients: {
      count: 1,
      critical: 1,
      specialty: "general",
    },
  },
  "rd-alkohol": {
    type: "medizinisch",
    code: "RD1",
    title: "Hilflose Person",
    report:
      "Hier sitzt jemand auf dem Boden, ist stark benommen und braucht Hilfe.",
    needs: {
      RTW: 1,
    },
    weight: 16,
    priority: 2,
    workSeconds: 240,
    patients: {
      count: 1,
      critical: 0,
      specialty: "general",
    },
  },
  "rd-psychisch": {
    type: "medizinisch",
    code: "RD2",
    title: "Psychischer Ausnahmezustand",
    report:
      "Eine Person ist sehr aufgebracht und droht, sich selbst etwas anzutun.",
    needs: {
      RTW: 1,
      NEF: 1,
      FuStW: 1,
    },
    weight: 10,
    priority: 1,
    workSeconds: 240,
    patients: {
      count: 1,
      critical: 1,
      specialty: "general",
    },
  },
  "vu-rad": {
    type: "vu",
    code: "VU2",
    title: "Fahrradunfall",
    report:
      "Ein Radfahrer ist gestürzt. Er hat sich verletzt und liegt auf der Straße.",
    needs: {
      RTW: 1,
      FuStW: 1,
    },
    weight: 8,
    priority: 2,
    workSeconds: 240,
    patients: {
      count: 1,
      critical: 0,
      specialty: "trauma",
    },
  },
  "vu-motorrad": {
    type: "vu",
    code: "VU2",
    title: "Motorradunfall",
    report:
      "Ein Motorradfahrer ist schwer gestürzt. Er liegt neben seinem Motorrad.",
    needs: {
      RTW: 1,
      FuStW: 1,
      NEF: 1,
      HLF: 1,
    },
    weight: 8,
    priority: 1,
    workSeconds: 240,
    patients: {
      count: 1,
      critical: 1,
      specialty: "trauma",
    },
  },
  "vu-fussgaenger": {
    type: "vu",
    code: "VU2",
    title: "Fußgänger angefahren",
    report:
      "Ein Auto hat einen Fußgänger erfasst. Die Person liegt verletzt auf der Straße.",
    needs: {
      RTW: 1,
      FuStW: 1,
      NEF: 1,
      HLF: 1,
    },
    weight: 8,
    priority: 1,
    workSeconds: 240,
    patients: {
      count: 1,
      critical: 1,
      specialty: "trauma",
    },
  },
  "vu-lkw": {
    type: "vu",
    code: "TH3",
    title: "LKW-Unfall · Eingeklemmt",
    report:
      "Ein Lastwagen ist von der Straße abgekommen. Der Fahrer ist eingeklemmt.",
    needs: {
      RTW: 1,
      FuStW: 1,
      NEF: 1,
      HLF: 1,
    },
    weight: 8,
    priority: 1,
    workSeconds: 240,
    patients: {
      count: 1,
      critical: 1,
      specialty: "trauma",
    },
  },
  "vu-mehrere": {
    type: "vu",
    code: "VU2",
    title: "Mehrere PKW beteiligt",
    report:
      "Mehrere Autos sind zusammengestoßen. Mindestens drei Menschen sind verletzt.",
    needs: {
      RTW: 3,
      FuStW: 1,
      NEF: 1,
      HLF: 1,
    },
    weight: 8,
    priority: 1,
    workSeconds: 240,
    patients: {
      count: 3,
      critical: 1,
      specialty: "trauma",
    },
  },
  "pol-einbruch": {
    type: "polizei",
    code: "POL1",
    title: "Einbruch entdeckt",
    report:
      "Meine Wohnungstür wurde aufgebrochen. Ich weiß nicht, ob noch jemand drinnen ist.",
    needs: {
      FuStW: 2,
    },
    weight: 12,
    priority: 2,
    workSeconds: 360,
  },
  "pol-diebstahl": {
    type: "polizei",
    code: "POL1",
    title: "Diebstahl",
    report:
      "Mein Fahrrad wurde gerade gestohlen. Ich habe die Person noch gesehen.",
    needs: {
      FuStW: 1,
    },
    weight: 12,
    priority: 2,
    workSeconds: 360,
  },
  "pol-randale": {
    type: "polizei",
    code: "POL2",
    title: "Auseinandersetzung mit Verletzten",
    report: "Mehrere Menschen schlagen aufeinander ein. Zwei sind verletzt.",
    needs: {
      FuStW: 3,
      RTW: 2,
    },
    weight: 12,
    priority: 1,
    workSeconds: 360,
    patients: {
      count: 2,
      critical: 1,
      specialty: "trauma",
    },
  },
  "pol-vermisst": {
    type: "polizei",
    code: "POL2",
    title: "Vermisste Person",
    report:
      "Unser Angehöriger wird vermisst. Er ist orientierungslos und wir finden ihn nicht.",
    needs: {
      FuStW: 2,
      DHuFü: 1,
    },
    weight: 12,
    priority: 2,
    workSeconds: 360,
  },
  "pol-verkehr": {
    type: "polizei",
    code: "POL1",
    title: "Gefährliche Verkehrsstörung",
    report:
      "Ein Fahrzeug blockiert die Straße. Die anderen müssen gefährlich ausweichen.",
    needs: {
      FuStW: 1,
    },
    weight: 12,
    priority: 2,
    workSeconds: 360,
  },
  "pol-bedrohung": {
    type: "polizei",
    code: "POL3",
    title: "Bewaffnete Bedrohung",
    report:
      "Ein Mann bedroht andere mit einer Waffe. Ich habe mich in Sicherheit gebracht.",
    needs: {
      FuStW: 3,
      SEK: 1,
      RTW: 1,
    },
    weight: 2,
    priority: 1,
    workSeconds: 360,
  },
  "pol-geisel": {
    type: "polizei",
    code: "POL3",
    title: "Geisellage",
    report:
      "Eine Person hält einen Menschen fest und droht ihm mit einer Waffe.",
    needs: {
      FuStW: 4,
      SEK: 2,
      ELW: 1,
      RTW: 2,
      NEF: 1,
    },
    weight: 2,
    priority: 1,
    workSeconds: 360,
    patients: {
      count: 1,
      critical: 1,
      specialty: "trauma",
    },
  },
  "pol-gruppe": {
    type: "polizei",
    code: "POL3",
    title: "Große gewalttätige Auseinandersetzung",
    report:
      "Hier kämpfen große Gruppen gegeneinander. Mehrere Menschen sind verletzt.",
    needs: {
      FuStW: 4,
      BePo: 2,
      RTW: 4,
      NEF: 1,
    },
    weight: 2,
    priority: 1,
    workSeconds: 360,
    patients: {
      count: 4,
      critical: 1,
      specialty: "trauma",
    },
  },
  "th-tuer": {
    type: "unwetter",
    code: "TH1",
    title: "Notfalltüröffnung",
    report:
      "Mein Nachbar ruft hinter der verschlossenen Tür um Hilfe und kann nicht öffnen.",
    needs: {
      HLF: 1,
      RTW: 1,
    },
    weight: 10,
    priority: 1,
    workSeconds: 360,
    patients: {
      count: 1,
      critical: 1,
      specialty: "trauma",
    },
  },
  "th-aufzug": {
    type: "unwetter",
    code: "TH1",
    title: "Personen im Aufzug",
    report: "Wir stecken im Aufzug fest. Im Moment ist niemand verletzt.",
    needs: {
      HLF: 1,
    },
    weight: 10,
    priority: 2,
    workSeconds: 360,
  },
  "th-oel": {
    type: "unwetter",
    code: "TH1",
    title: "Ölspur",
    report: "Eine lange Ölspur zieht sich über die Fahrbahn.",
    needs: {
      LF: 1,
      FuStW: 1,
    },
    weight: 10,
    priority: 2,
    workSeconds: 360,
  },
  "th-gas": {
    type: "unwetter",
    code: "TH3",
    title: "Gasgeruch im Gebäude",
    report:
      "Im Haus riecht es stark nach Gas. Wir sind draußen, eine Person ist benommen.",
    needs: {
      HLF: 2,
      ELW: 1,
      FuStW: 2,
      RTW: 1,
      NEF: 1,
    },
    weight: 10,
    priority: 1,
    workSeconds: 360,
    patients: {
      count: 1,
      critical: 1,
      specialty: "trauma",
    },
  },
  "th-einsturz": {
    type: "unwetter",
    code: "TH5",
    title: "Teileinsturz eines Gebäudes",
    report:
      "Ein Teil des Hauses ist eingestürzt. Drei Menschen werden vermisst.",
    needs: {
      HLF: 2,
      RW: 1,
      GKW: 2,
      ELW: 1,
      RTW: 3,
      NEF: 2,
    },
    weight: 10,
    priority: 1,
    workSeconds: 360,
    patients: {
      count: 3,
      critical: 1,
      specialty: "trauma",
    },
  },
  "th-wasser": {
    type: "unwetter",
    code: "TH2",
    title: "Überfluteter Keller",
    report:
      "Der Keller läuft nach dem Starkregen voll. Alle Personen sind draußen.",
    needs: {
      HLF: 1,
      GKW: 1,
    },
    weight: 10,
    priority: 2,
    workSeconds: 360,
  },
  "th-baum": {
    type: "unwetter",
    code: "TH2",
    title: "Baum auf Fahrzeug",
    report: "Ein Baum liegt auf einem Auto. Der Fahrer kommt nicht heraus.",
    needs: {
      HLF: 1,
      RW: 1,
      RTW: 1,
      NEF: 1,
    },
    weight: 10,
    priority: 1,
    workSeconds: 360,
    patients: {
      count: 1,
      critical: 1,
      specialty: "trauma",
    },
  },
  "wasser-bootsunfall": {
    type: "wasser",
    code: "WR3",
    title: "Bootsunfall",
    report:
      "Ein Boot ist gekentert. Drei Menschen sind im Wasser und kommen nicht ans Ufer.",
    needs: {
      "GW-Wasser": 1,
      RTB: 1,
      RTW: 3,
      NEF: 1,
      HLF: 1,
    },
    weight: 3,
    priority: 1,
    workSeconds: 420,
    patients: {
      count: 3,
      critical: 1,
      specialty: "general",
    },
  },
  "berg-absturz": {
    type: "berg",
    code: "BR3",
    title: "Absturz im Gelände",
    report:
      "Eine Person ist einen Abhang hinuntergestürzt. Wir kommen nicht sicher zu ihr.",
    needs: {
      Bergrettung: 1,
      RTH: 1,
    },
    weight: 3,
    priority: 1,
    workSeconds: 420,
    patients: {
      count: 1,
      critical: 1,
      specialty: "trauma",
    },
  },
};
